
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { getUserPinOrThrow, shouldUsePinMode } from '@/utils/pinModeUtils';

export interface ProjectFormData {
  name: string;
  description?: string;
  client_name?: string;
  address?: string;
  coordinates_lat?: number;
  coordinates_lng?: number;
  status: 'active' | 'completed' | 'on-hold';
  estimated_hours?: number;
  start_date?: string;
  end_date?: string;
}

export interface Project extends ProjectFormData {
  id: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Type helper pour les données venant de Supabase
interface SupabaseProject {
  id: string;
  name: string;
  description: string | null;
  client_name: string | null;
  address: string | null;
  coordinates_lat: number | null;
  coordinates_lng: number | null;
  status: string | null;
  estimated_hours: number | null;
  start_date?: string | null;
  end_date?: string | null;
  is_active: boolean | null;
  created_at: string | null;
  updated_at: string | null;
}

// Fonction pour transformer les données Supabase en Project
const transformSupabaseProject = (data: SupabaseProject): Project => {
  return {
    id: data.id,
    name: data.name,
    description: data.description || undefined,
    client_name: data.client_name || undefined,
    address: data.address || undefined,
    coordinates_lat: data.coordinates_lat || undefined,
    coordinates_lng: data.coordinates_lng || undefined,
    status: (data.status || 'active') as 'active' | 'completed' | 'on-hold',
    estimated_hours: data.estimated_hours || undefined,
    start_date: data.start_date || undefined,
    end_date: data.end_date || undefined,
    is_active: data.is_active ?? true,
    created_at: data.created_at || '',
    updated_at: data.updated_at || '',
  };
};

// Fonction pour préparer les données pour Supabase
const prepareProjectDataForSupabase = (data: ProjectFormData) => {
  return {
    name: data.name, // Toujours requis
    description: data.description || null,
    client_name: data.client_name || null,
    address: data.address || null,
    coordinates_lat: data.coordinates_lat || null,
    coordinates_lng: data.coordinates_lng || null,
    status: data.status,
    estimated_hours: data.estimated_hours || null,
    // NB: la table `projects` ne contient pas (actuellement) start_date/end_date.
    is_active: true,
  };
};

export const useProjectManagement = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const fetchProjects = async () => {
    try {
      setLoading(true);
      console.log('Récupération des projets...');

      let data: any[] | null = null;
      let error: any = null;

      if (shouldUsePinMode()) {
        const pin = getUserPinOrThrow();
        const res = await supabase.rpc('pin_list_projects', { p_pin: pin });
        data = (res.data as any[]) || [];
        error = res.error;
      } else {
        const res = await supabase.from('projects').select('*').order('created_at', { ascending: false });
        data = (res.data as any[]) || [];
        error = res.error;
      }

      if (error) {
        console.error('Erreur Supabase lors du chargement:', error);
        throw error;
      }
      
      console.log('Données brutes de Supabase:', data);
      const transformedProjects = (data || []).map((row: any) => {
        // `pin_list_projects` retourne un subset de colonnes.
        // On normalise vers le type `Project` attendu.
        return transformSupabaseProject({
          id: row.id,
          name: row.name,
          description: row.description ?? null,
          client_name: row.client_name ?? null,
          address: row.address ?? null,
          coordinates_lat: row.coordinates_lat ?? null,
          coordinates_lng: row.coordinates_lng ?? null,
          status: row.status ?? null,
          estimated_hours: row.estimated_hours ?? null,
          is_active: row.is_active ?? true,
          created_at: row.created_at ?? null,
          updated_at: row.updated_at ?? null,
        });
      });
      console.log('Projets transformés:', transformedProjects);
      
      setProjects(transformedProjects);
    } catch (error) {
      console.error('Erreur lors du chargement des projets:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les projets",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createProject = async (projectData: ProjectFormData) => {
    try {
      setSubmitting(true);
      console.log('Création du projet avec les données:', projectData);
      
      let transformedProject: Project;

      if (shouldUsePinMode()) {
        const pin = getUserPinOrThrow();
        const { data: projectId, error } = await supabase.rpc('pin_create_project', {
          p_pin: pin,
          p_name: projectData.name,
          p_description: projectData.description ?? null,
          p_client_name: projectData.client_name ?? null,
          p_address: projectData.address ?? null,
          p_coordinates_lat: projectData.coordinates_lat ?? null,
          p_coordinates_lng: projectData.coordinates_lng ?? null,
          p_status: projectData.status,
          p_estimated_hours: projectData.estimated_hours ?? null,
        });

        if (error) {
          console.error('Erreur Supabase lors de la création:', error);
          throw error;
        }
        if (!projectId) throw new Error('Aucun ID projet retourné');

        transformedProject = {
          id: projectId as unknown as string,
          name: projectData.name,
          description: projectData.description,
          client_name: projectData.client_name,
          address: projectData.address,
          coordinates_lat: projectData.coordinates_lat,
          coordinates_lng: projectData.coordinates_lng,
          status: projectData.status,
          estimated_hours: projectData.estimated_hours,
          start_date: projectData.start_date,
          end_date: projectData.end_date,
          is_active: true,
          created_at: '',
          updated_at: '',
        };
      } else {
        const supabaseData = prepareProjectDataForSupabase(projectData);
        console.log('Données préparées pour Supabase:', supabaseData);

        const { data, error } = await supabase
          .from('projects')
          .insert(supabaseData) // Passer l'objet directement, pas dans un tableau
          .select()
          .single();

        if (error) {
          console.error('Erreur Supabase lors de la création:', error);
          throw error;
        }

        console.log('Projet créé avec succès:', data);
        transformedProject = transformSupabaseProject(data as any);
      }

      setProjects(prev => [transformedProject, ...prev]);
      
      toast({
        title: "Succès",
        description: "Projet créé avec succès",
      });

      return transformedProject;
    } catch (error) {
      console.error('Erreur lors de la création du projet:', error);
      toast({
        title: "Erreur",
        description: error instanceof Error ? error.message : "Impossible de créer le projet",
        variant: "destructive",
      });
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  const updateProject = async (id: string, projectData: Partial<ProjectFormData>) => {
    try {
      setSubmitting(true);
      console.log('Mise à jour du projet:', id, projectData);
      
      if (shouldUsePinMode()) {
        const pin = getUserPinOrThrow();
        const { error } = await supabase.rpc('pin_update_project', {
          p_pin: pin,
          p_project_id: id,
          p_name: projectData.name ?? null,
          p_description: projectData.description ?? null,
          p_client_name: projectData.client_name ?? null,
          p_address: projectData.address ?? null,
          p_coordinates_lat: projectData.coordinates_lat ?? null,
          p_coordinates_lng: projectData.coordinates_lng ?? null,
          p_status: projectData.status ?? null,
          p_estimated_hours: projectData.estimated_hours ?? null,
          p_is_active: null,
        });
        if (error) {
          console.error('Erreur Supabase lors de la mise à jour:', error);
          throw error;
        }

        setProjects((prev) =>
          prev.map((p) =>
            p.id === id
              ? {
                  ...p,
                  ...projectData,
                }
              : p
          )
        );
      } else {
        const supabaseData = prepareProjectDataForSupabase(projectData as ProjectFormData);

        const { data, error } = await supabase
          .from('projects')
          .update(supabaseData)
          .eq('id', id)
          .select()
          .single();

        if (error) {
          console.error('Erreur Supabase lors de la mise à jour:', error);
          throw error;
        }

        const transformedProject = transformSupabaseProject(data as any);
        setProjects((prev) => prev.map((p) => (p.id === id ? transformedProject : p)));
      }
      
      toast({
        title: "Succès",
        description: "Projet mis à jour avec succès",
      });

      return projects.find((p) => p.id === id) as Project;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du projet:', error);
      toast({
        title: "Erreur",
        description: error instanceof Error ? error.message : "Impossible de mettre à jour le projet",
        variant: "destructive",
      });
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  const deleteProject = async (id: string) => {
    try {
      setSubmitting(true);
      console.log('Suppression du projet:', id);

      if (shouldUsePinMode()) {
        const pin = getUserPinOrThrow();
        const { error } = await supabase.rpc('pin_delete_project', {
          p_pin: pin,
          p_project_id: id,
        });
        if (error) {
          console.error('Erreur Supabase lors de la suppression:', error);
          throw error;
        }
      } else {
        const { error } = await supabase.from('projects').delete().eq('id', id);

        if (error) {
          console.error('Erreur Supabase lors de la suppression:', error);
          throw error;
        }
      }

      setProjects(prev => prev.filter(p => p.id !== id));
      toast({
        title: "Succès",
        description: "Projet supprimé avec succès",
      });
    } catch (error) {
      console.error('Erreur lors de la suppression du projet:', error);
      toast({
        title: "Erreur",
        description: error instanceof Error ? error.message : "Impossible de supprimer le projet",
        variant: "destructive",
      });
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  return {
    projects,
    loading,
    submitting,
    createProject,
    updateProject,
    deleteProject,
    refetch: fetchProjects,
  };
};
